/**
 * §P.3 · Los descuentos de una cuota.
 *
 * Vive debajo del desglose porque es el **por qué** de lo que el desglose enseña.
 * Cinco cosas del contrato que cambian lo que se ve:
 *
 * **Sin `ends_on`, de por vida**, y es el caso por defecto. El descuento de
 * hermanos dura mientras haya dos hermanos, no hasta una fecha; la promoción de
 * septiembre sí la lleva. Así que el formulario propone «de por vida» y la fecha
 * es lo que se añade, no lo que se quita.
 *
 * **`live` dice cuáles cuentan hoy.** Los pasados se quedan y se pintan aparte,
 * porque son lo que explica por qué esa familia pagó 24,65 € en marzo.
 *
 * **`409 discount_overlaps` no es «ya lo tiene».** Lo que se prohíbe es el
 * solapamiento, no la repetición: el mismo descuento **sí** puede volver a
 * aplicarse más adelante. El aviso lo dice así, porque «ya lo tiene» llevaría al
 * club a no volver a intentarlo en septiembre.
 *
 * **`422 discount_rule_retired`**: la regla está retirada, no se le puede dar a
 * nadie nuevo. El desplegable ya solo enseña las activas, así que esto solo salta
 * si alguien retira la regla mientras el formulario está abierto.
 *
 * **Quitarlo borra la fila de verdad**, y es la única baja de §P.3 que borra: un
 * descuento aplicado por error no es historia que conservar, es un error de
 * teclado. Lo que sí es historia —cuánto pagó esa familia en marzo— vive en las
 * líneas del recibo, que guardan su importe copiado al emitirse. Y por eso se
 * pregunta antes.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import { aProblema, ErrorDeApi, type Problema } from '@/shared/api/errores';
import { Cargando, Error as PanelError } from '@/shared/ui/Estados';
import { fecha, fechaParaInput } from '@/shared/ui/formato';
import { FaltaAntes } from '@/shared/ui/FaltaAntes';
import {
  etiquetaDeValor,
  finDeCurso,
  type DescuentoAplicado,
  type ReglaDeDescuento,
} from '@/club/descuentos/tipos';

export function DescuentosDeLaCuota({
  club,
  cuotaId,
  puedeEditar,
}: {
  club: string;
  cuotaId: string;
  puedeEditar: boolean;
}) {
  const cache = useQueryClient();
  const [aplicando, setAplicando] = useState(false);
  const [reglaId, setReglaId] = useState('');
  const [caduca, setCaduca] = useState(false);
  const [hasta, setHasta] = useState(() => finDeCurso());
  const [problema, setProblema] = useState<Problema | null>(null);
  const [solapa, setSolapa] = useState<Problema | null>(null);
  const [editando, setEditando] = useState<DescuentoAplicado | null>(null);
  const [quitando, setQuitando] = useState<DescuentoAplicado | null>(null);

  const aplicados = useQuery({
    queryKey: ['descuentos-cuota', club, cuotaId],
    queryFn: async () => {
      const { data, error } = await api.v1
        .clubs({ club })
        .subscriptions({ subscriptionId: cuotaId })
        .discounts.get();
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as DescuentoAplicado[];
    },
  });

  // El catálogo, solo activas: una retirada responde `422` al aplicarla, así que
  // no se ofrece.
  const reglas = useQuery({
    queryKey: ['descuentos', club, false],
    enabled: aplicando,
    queryFn: async () => {
      const { data, error } = await api.v1
        .clubs({ club })
        ['discount-rules'].get({ query: {} });
      if (error) throw new ErrorDeApi(aProblema(error));
      return (data as unknown as ReglaDeDescuento[]).filter((r) => r.active);
    },
  });

  const refrescar = () => {
    void cache.invalidateQueries({
      queryKey: ['descuentos-cuota', club, cuotaId],
    });
    // El importe lo calcula el servidor, así que hay que volver a pedirlo: un
    // descuento nuevo cambia el total y el desglose está justo encima.
    void cache.invalidateQueries({ queryKey: ['desglose', club, cuotaId] });
  };

  const aplicar = useMutation({
    mutationFn: async () => {
      const r = await api.v1
        .clubs({ club })
        .subscriptions({ subscriptionId: cuotaId })
        .discounts.post({
          discount_rule_id: reglaId,
          // `null` explícito: es «de por vida», que es el caso por defecto.
          ends_on: caduca ? hasta : null,
        });
      if (r.error) throw new ErrorDeApi(aProblema(r.error));
      return r.data;
    },
    onSuccess: () => {
      setAplicando(false);
      setReglaId('');
      setCaduca(false);
      refrescar();
    },
    onError: (e) => {
      const p = aProblema(e);
      if (p.code === 'discount_overlaps') setSolapa(p);
      else setProblema(p);
    },
  });

  const cambiarCaducidad = useMutation({
    mutationFn: async (nueva: string | null) => {
      const r = await api.v1
        .clubs({ club })
        ['subscription-discounts']({ appliedId: editando!.id })
        .patch({ ends_on: nueva });
      if (r.error) throw new ErrorDeApi(aProblema(r.error));
      return r.data;
    },
    onSuccess: () => {
      setEditando(null);
      refrescar();
    },
    onError: (e) => setProblema(aProblema(e)),
  });

  const quitar = useMutation({
    mutationFn: async (id: string) => {
      const r = await api.v1
        .clubs({ club })
        ['subscription-discounts']({ appliedId: id })
        .delete();
      if (r.error) throw new ErrorDeApi(aProblema(r.error));
      return r.data;
    },
    onSuccess: () => {
      setQuitando(null);
      refrescar();
    },
    onError: (e) => setProblema(aProblema(e)),
  });

  if (aplicados.isPending) {
    return <Cargando filas={1} etiqueta="Cargando descuentos" />;
  }

  if (aplicados.isError) {
    return (
      <PanelError
        problema={aProblema(aplicados.error)}
        reintentar={() => void aplicados.refetch()}
      />
    );
  }

  const vivos = (aplicados.data ?? []).filter((d) => d.live);
  const pasados = (aplicados.data ?? []).filter((d) => !d.live);

  // ── Quitar, con la pregunta delante ──────────────────────────────────────
  if (quitando) {
    return (
      <div className="hueco hueco-confirmar">
        <h3>¿Quitarle el descuento «{quitando.name}»?</h3>
        <ul className="lista-clara">
          <li>
            <strong>Esta baja borra la fila.</strong> Es la única de tarifas y
            descuentos que borra de verdad: un descuento aplicado por error no
            es historia que conservar, es un error de teclado.
          </li>
          <li>
            <strong>Si solo quieres que deje de aplicarse</strong>, ponle
            caducidad en vez de quitarlo: así se sigue entendiendo lo que pagó
            hasta entonces.
          </li>
        </ul>

        {problema && <PanelError problema={problema} />}

        <div className="hueco-acciones">
          <button
            type="button"
            className="btn-peligro"
            disabled={quitar.isPending}
            onClick={() => quitar.mutate(quitando.id)}
          >
            {quitar.isPending ? 'Quitando…' : 'Sí, quitar el descuento'}
          </button>
          <button
            type="button"
            className="btn-plano"
            onClick={() => setQuitando(null)}
          >
            Dejarlo
          </button>
        </div>
      </div>
    );
  }

  // ── Cambiar la caducidad ─────────────────────────────────────────────────
  if (editando) {
    return (
      <form
        className="formulario"
        onSubmit={(e) => {
          e.preventDefault();
          cambiarCaducidad.mutate(hasta === '' ? null : hasta);
        }}
      >
        <h3>Caducidad de «{editando.name}»</h3>

        <div className="rejilla">
          <label>
            Hasta
            <input
              type="date"
              required
              value={hasta}
              onChange={(e) => setHasta(e.target.value)}
            />
          </label>
        </div>

        <div className="nota">
          <strong>Volverlo de por vida es un botón, no vaciar la fecha.</strong>{' '}
          Y es un cambio de verdad: el club que decide que la promoción se queda
          tiene que poder decirlo.
        </div>

        {problema && <PanelError problema={problema} />}

        <div className="acciones">
          <button type="submit" disabled={cambiarCaducidad.isPending}>
            {cambiarCaducidad.isPending ? 'Guardando…' : 'Guardar la caducidad'}
          </button>
          {/*
            «De por vida» tiene su propio botón y no se hace vaciando el campo.
            Se probó en el navegador: al borrar un `<input type="date">` queda a
            medias —«dd/10/2026»—, el navegador lo marca `badInput` y **el envío
            no llega a salir**, sin un solo mensaje. El botón manda `null` y no
            depende de que el campo se pueda vaciar.
          */}
          {editando.ends_on && (
            <button
              type="button"
              className="btn-plano"
              disabled={cambiarCaducidad.isPending}
              onClick={() => cambiarCaducidad.mutate(null)}
            >
              Que no caduque nunca
            </button>
          )}
          <button
            type="button"
            className="btn-plano"
            onClick={() => setEditando(null)}
          >
            Cancelar
          </button>
        </div>
      </form>
    );
  }

  // ── Aplicar uno ──────────────────────────────────────────────────────────
  if (aplicando) {
    return (
      <form
        className="formulario"
        onSubmit={(e) => {
          e.preventDefault();
          aplicar.mutate();
        }}
      >
        <h3>Aplicar un descuento</h3>

        {reglas.isPending && <Cargando filas={1} etiqueta="Cargando" />}

        {reglas.data?.length === 0 && (
          <FaltaAntes accion="Ir a Descuentos" destino="/cobros/descuentos">
            El club no tiene descuentos activos. Se crean en{' '}
            <strong>Descuentos</strong>, y desde aquí solo se aplican.
          </FaltaAntes>
        )}

        {reglas.data && reglas.data.length > 0 && (
          <>
            <div className="rejilla">
              <label>
                Descuento
                <select
                  required
                  value={reglaId}
                  onChange={(e) => setReglaId(e.target.value)}
                >
                  <option value="">Elige un descuento</option>
                  {reglas.data.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.name} · {etiquetaDeValor(r.kind, r.value)}
                    </option>
                  ))}
                </select>
              </label>

              <label className="opcion">
                <input
                  type="checkbox"
                  checked={caduca}
                  onChange={(e) => setCaduca(e.target.checked)}
                />
                Que caduque en una fecha
              </label>

              {caduca && (
                <label>
                  Hasta
                  <input
                    type="date"
                    required
                    value={hasta}
                    onChange={(e) => setHasta(e.target.value)}
                  />
                </label>
              )}
            </div>

            {!caduca && (
              <div className="nota">
                <strong>De por vida.</strong> Es el caso por defecto y el
                correcto para el descuento de hermanos: dura mientras haya dos
                hermanos, no hasta una fecha. Una promoción de septiembre sí
                lleva fecha.
              </div>
            )}
          </>
        )}

        {/*
          El solapamiento no es «ya lo tiene»: el mismo descuento puede volver a
          aplicarse más adelante, solo no encima de sí mismo.
        */}
        {solapa && (
          <div className="aviso" role="status">
            <strong>{solapa.title}</strong> {solapa.detail}
            <p className="tenue">
              Mira en la lista cuándo termina el que ya tiene.
            </p>
          </div>
        )}

        {problema && <PanelError problema={problema} />}

        <div className="acciones">
          <button
            type="submit"
            disabled={
              aplicar.isPending || !reglaId || reglas.data?.length === 0
            }
          >
            {aplicar.isPending ? 'Aplicando…' : 'Aplicar el descuento'}
          </button>
          <button
            type="button"
            className="btn-plano"
            onClick={() => {
              setAplicando(false);
              setSolapa(null);
              setProblema(null);
            }}
          >
            Cancelar
          </button>
        </div>
      </form>
    );
  }

  // ── La lista ─────────────────────────────────────────────────────────────
  return (
    <div className="descuentos-cuota">
      {vivos.length === 0 && pasados.length === 0 ? (
        <p className="tenue">
          Sin descuentos: paga la tarifa entera.{' '}
          {puedeEditar && 'Se aplican de uno en uno y se acumulan.'}
        </p>
      ) : (
        <>
          {vivos.length > 0 && (
            <ul className="descuentos-lista">
              {vivos.map((d) => (
                <li key={d.id}>
                  <span>
                    <strong>{d.name}</strong>{' '}
                    <span className="tenue">
                      {etiquetaDeValor(d.kind, d.value)} ·{' '}
                      {d.ends_on ? (
                        <>hasta el {fecha(d.ends_on)}</>
                      ) : (
                        'de por vida'
                      )}
                    </span>
                  </span>

                  {puedeEditar && (
                    <span className="tutor-acciones" style={{ margin: 0 }}>
                      <button
                        type="button"
                        className="btn-plano"
                        onClick={() => {
                          setEditando(d);
                          // Uno de por vida entra con una propuesta y no vacío:
                          // el campo es `required` y un vacío bloquearía el
                          // envío sin explicar nada.
                          setHasta(fechaParaInput(d.ends_on) || finDeCurso());
                          setProblema(null);
                        }}
                      >
                        Caducidad
                      </button>
                      <button
                        type="button"
                        className="btn-plano"
                        onClick={() => {
                          setQuitando(d);
                          setProblema(null);
                        }}
                      >
                        Quitar
                      </button>
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}

          {/* El histórico se queda: es lo que explica el recibo de marzo. */}
          {pasados.length > 0 && (
            <details className="descuentos-pasados">
              <summary>
                {pasados.length === 1
                  ? 'Tuvo 1 descuento más'
                  : `Tuvo ${pasados.length} descuentos más`}
              </summary>
              <ul className="descuentos-lista">
                {pasados.map((d) => (
                  <li key={d.id} className="fila-fuera">
                    <span>
                      {d.name}{' '}
                      <span className="tenue">
                        {etiquetaDeValor(d.kind, d.value)} ·{' '}
                        {fecha(d.starts_on)} — {fecha(d.ends_on)}
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
            </details>
          )}
        </>
      )}

      {problema && <PanelError problema={problema} />}

      {puedeEditar && (
        <button
          type="button"
          className="btn-plano"
          onClick={() => {
            setAplicando(true);
            setSolapa(null);
            setProblema(null);
            setHasta(finDeCurso());
          }}
        >
          Aplicar un descuento
        </button>
      )}
    </div>
  );
}
